import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type PostPurchaseRuleDetails = OutputRouter['postPurchaseRules']['getById']['rule']
export type PostPurchaseRulesListOutput = OutputRouter['postPurchaseRules']['list']
export type PostPurchaseRuleItem = PostPurchaseRulesListOutput['items'][number]
export type UpsertPostPurchaseRuleOutput = OutputRouter['postPurchaseRules']['upsert']
export type DeletePostPurchaseRulesOutput = OutputRouter['postPurchaseRules']['delete']
export type ListPostOrderPurchaseOffersOutput = OutputRouter['postPurchaseRules']['listPostOrderPurchaseOffers']
export type GetPostPurchaseConfigOutput = OutputRouter['postPurchaseRules']['getPostPurchaseConfig']

// Input types
export type GetPostPurchaseRuleInput = InputRouter['postPurchaseRules']['getById']
export type ListPostPurchaseRulesInput = InputRouter['postPurchaseRules']['list']
export type UpsertPostPurchaseRuleInput = InputRouter['postPurchaseRules']['upsert']
export type DeletePostPurchaseRulesInput = InputRouter['postPurchaseRules']['delete']
export type ListPostOrderPurchaseOffersInput = InputRouter['postPurchaseRules']['listPostOrderPurchaseOffers']
export type GetPostPurchaseConfigInput = InputRouter['postPurchaseRules']['getPostPurchaseConfig']

// Query hooks
export const usePostPurchaseRule = (id: string, enabled = true) => {
	return trpc.postPurchaseRules.getById.useQuery(
		{ id },
		{
			enabled: enabled && !!id,
			retry: 2,
			staleTime: 30000,
		},
	)
}

export const usePostPurchaseRulesList = (params: ListPostPurchaseRulesInput, options?: {
	enabled?: boolean
	refetchOnMount?: boolean | 'always'
	refetchOnWindowFocus?: boolean
}) => {
	const { enabled = true, ...otherOptions } = options || {}
	return trpc.postPurchaseRules.list.useQuery(params, {
		enabled,
		...otherOptions,
	})
}

export const useListPostOrderPurchaseOffers = (params: ListPostOrderPurchaseOffersInput, enabled = true) => {
	return trpc.postPurchaseRules.listPostOrderPurchaseOffers.useQuery(params, {
		enabled: enabled && !!params.orderId,
	})
}

export const useGetPostPurchaseConfig = (params: GetPostPurchaseConfigInput, enabled = true) => {
	return trpc.postPurchaseRules.getPostPurchaseConfig.useQuery(params, {
		enabled: enabled && !!params.storeId,
	})
}

// Mutation hooks
export const useUpsertPostPurchaseRule = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.postPurchaseRules.upsert.useMutation({
		onSuccess: () => {
			utils.postPurchaseRules.list.invalidate()
			toast({
				title: 'Success',
				description: 'Post purchase rule saved successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to save post purchase rule',
				variant: 'destructive',
			})
		},
	})
}

export const useDeletePostPurchaseRules = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.postPurchaseRules.delete.useMutation({
		onSuccess: () => {
			utils.postPurchaseRules.list.invalidate()
			toast({
				title: 'Success',
				description: 'Post purchase rule(s) deleted successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to delete post purchase rule(s)',
				variant: 'destructive',
			})
		},
	})
}

